import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TemplatePreviewPanel } from '../../features/templates/ui';
import {
  selectTemplatesHasLoadedOnce,
  selectTemplatesItems,
  selectTemplatesListStatus,
  selectTemplatesTabs,
  selectTemplatesPage,
  selectTemplatesPageSize,
  selectTemplatesSearch,
  selectTemplatesFilters,
  selectTemplatesTotalPages,
  selectTemplatesTenantLogo,
  selectTemplatesTenantSettingsStatus,
  templatesPageChanged,
  templatesPageSizeChanged,
} from '../../features/templates/store/templatesSlice';
import { SpinnerWrap } from '../../shared/ui/spinner';
import {
  useApiReady,
  useFromLocationState,
  usePageTitle,
  usePreloadImage,
  useScrollToTop,
} from '../../shared/hooks';
import { useTenantSettingsLoader } from '../../features/templates/hooks/useTenantSettingsLoader';
import { useTemplatePreview } from '../../features/templates/hooks/useTemplatePreview';
import { LOADING_STATUS } from '../../shared/constants/loadingStatus';
import { getIsTemplatesInitialLoading } from './lib/getIsTemplatesInitialLoading';
import { getShowTemplatesCardsSpinner } from './lib/getShowTemplatesCardsSpinner';
import { useTemplatesTabPrefetch } from '../../features/templates/hooks/useTemplatesTabPrefetch';
import { TemplatesSubheader, StickyFiltersPanel, TemplatesList } from './ui';
import { EmptyState } from '../../shared/ui/empty-state/EmptyState';
import { CreateTemplateButton } from '../../features/templates/ui/create-template-button/CreateTemplateButton';
import styles from './TemplatesPage.module.css';
import { getTemplateCreateRoute, getTemplateEditRoute } from '../../shared/config/routes';
import { usePermissions } from '../../features/auth/hooks/usePermissions';
import layoutStyles from '../../shared/styles/layout.module.css';

export function TemplatesPage() {
  usePageTitle('Templates');
  useScrollToTop();
  const { previewTemplate, openPreview, closePreview } = useTemplatePreview();
  const { canEdit, canCreate } = usePermissions();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fromState = useFromLocationState();
  const apiReady = useApiReady();
  const tenantLogoUrl = useSelector(selectTemplatesTenantLogo);
  const listStatus = useSelector(selectTemplatesListStatus);
  const tenantSettingsStatus = useSelector(selectTemplatesTenantSettingsStatus);
  const hasLoadedOnce = useSelector(selectTemplatesHasLoadedOnce);
  const tabs = useSelector(selectTemplatesTabs);
  const items = useSelector(selectTemplatesItems);
  const page = useSelector(selectTemplatesPage);
  const pageSize = useSelector(selectTemplatesPageSize);
  const totalPages = useSelector(selectTemplatesTotalPages);
  const search = useSelector(selectTemplatesSearch);
  const filters = useSelector(selectTemplatesFilters);
  const isSearchActive = String(search ?? '').trim().length > 0;
  const isFiltersActive =
    (filters?.status?.length ?? 0) > 0 ||
    (filters?.type?.length ?? 0) > 0 ||
    (filters?.tags?.length ?? 0) > 0 ||
    (filters?.campaigns?.length ?? 0) > 0;
  const isQueryActive = isSearchActive || isFiltersActive;

  const isInitialLoading = getIsTemplatesInitialLoading({
    apiReady,
    hasLoadedOnce,
    isSearchActive: isQueryActive,
    itemsLength: items.length,
    listStatus,
    tenantSettingsStatus,
  });

  const showCardsSpinner = getShowTemplatesCardsSpinner({
    listStatus,
    itemsLength: items.length,
    isSearchActive: isQueryActive,
    hasLoadedOnce,
  });

  useTemplatesTabPrefetch(apiReady, tabs);
  useTenantSettingsLoader();

  usePreloadImage(tenantLogoUrl);

  const onCreateTemplate = (channelKey) => {
    navigate(getTemplateCreateRoute(channelKey), { state: fromState });
  };

  const onEditTemplate = (item) => {
    const templateId = item?.id;
    if (templateId == null) {
      return;
    }
    navigate(getTemplateEditRoute(item?.channel, templateId), { state: fromState });
  };

  const onCopyTemplate = (item) => {
    const templateId = item?.id;
    if (templateId == null) {
      return;
    }
    navigate(getTemplateCreateRoute(item?.channel), {
      state: { ...fromState, copyFromTemplateId: templateId },
    });
  };

  const templatesListProps = {
    items,
    showCardsSpinner,
    page,
    pageSize,
    totalPages,
    onPageChange: (nextPage) => dispatch(templatesPageChanged(nextPage)),
    onPageSizeChange: (nextSize) => dispatch(templatesPageSizeChanged(nextSize)),
    onCreateTemplate: canCreate ? onCreateTemplate : undefined,
    onEditTemplate: canEdit ? onEditTemplate : undefined,
    onCopyTemplate: canEdit ? onCopyTemplate : undefined,
    canEdit,
    onPreviewTemplate: openPreview,
  };

  return (
    <main>
      <TemplatesSubheader />

      <TemplatePreviewPanel
        open={previewTemplate !== null}
        onClose={closePreview}
        template={previewTemplate}
      />

      <div className={clsx(layoutStyles.pageContentWrap)}>
        {isInitialLoading ? (
          <SpinnerWrap ariaLabel="Loading templates" />
        ) : (
          <div className={clsx(styles.content)}>
            <StickyFiltersPanel />
            {items.length > 0 || isQueryActive || listStatus !== LOADING_STATUS.SUCCEEDED ? (
              <TemplatesList {...templatesListProps} />
            ) : (
              <EmptyState
                title="Speed up your work with templates"
                subtitle="Create templates to save time and reuse your content"
                ariaLabel="No templates"
              >
                {canCreate ? <CreateTemplateButton size="sm" onClick={onCreateTemplate} /> : null}
              </EmptyState>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
